import { useGlobalState } from '../state/useGlobalState';
import { useShallow } from 'zustand/react/shallow';
import { useStorePrimitive } from './useStore';
import type { SessionState } from '../types/types';

/**
 * A hook for safely accessing data from the session slice of the global state.
 * It uses a selector function to pick data and provides a default value if the
 * data is undefined or if an error occurs during selection (e.g., trying to
 * access a property on an undefined object).
 *
 * **ADR-001 Compliant**: Uses shallow equality to prevent unnecessary re-renders
 * when selecting objects or arrays.
 *
 * @param selector A function to select data from the session state.
 * @param defaultValue The default value to return if the selected data is undefined.
 * @returns The selected data or the default value.
 *
 * @see docs/adr/0001-zustand-state-management.md
 */
export function useSessionData<T, U extends T | null>(selector: (session: SessionState) => T, defaultValue: U): T | U {
    return useGlobalState(useShallow(state => {
        try {
            const value = selector(state.session);
            // Return the default value only if the selector returns undefined. Null is a valid value.
            return value ?? defaultValue;
        } catch {
            return defaultValue;
        }
    }));
}

/**
 * Hook to check if session data is still loading (not yet hydrated).
 * Returns a boolean primitive, so uses useStorePrimitive for ADR-001 compliance.
 */
export const useIsSessionLoading = () => useStorePrimitive(state => !state.session);